import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquarePlus, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";

const CATS = ["bug", "suggestion", "general"];

export default function Feedback() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [contact, setContact] = useState("");
  const [category, setCategory] = useState("bug");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (message.trim().length < 3) {
      toast.error(t("feedbackEmpty"));
      return;
    }
    setBusy(true);
    try {
      await api("/feedback", {
        method: "POST",
        body: JSON.stringify({
          message: message.trim(),
          category,
          contact: contact.trim() || null,
          platform: "web",
        }),
      });
      setDone(true);
    } catch (e) {
      toast.error(e.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center" data-testid="feedback-done">
        <CheckCircle2 className="mx-auto h-16 w-16 text-[#2E7D32]" />
        <h1 className="mt-4 text-2xl font-black text-ink">{t("feedbackThanks")}</h1>
        <Button onClick={() => navigate("/")} className="mt-6 h-11 rounded-full bg-brand px-8 font-bold hover:bg-brand-dark">
          {t("tabHome")}
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-10">
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1 text-sm font-semibold text-ink-muted hover:text-brand">
        <ArrowLeft size={16} /> {t("back")}
      </button>
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-tint text-brand"><MessageSquarePlus size={22} /></span>
        <div>
          <h1 className="text-2xl font-black text-ink">{t("feedbackTitle")}</h1>
          <p className="text-sm text-ink-muted">{t("feedbackSub")}</p>
        </div>
      </div>

      <div className="mt-6 flex gap-2">
        {CATS.map((c) => (
          <button
            key={c}
            data-testid={`feedback-cat-${c}`}
            onClick={() => setCategory(c)}
            className={`rounded-full px-4 py-2 text-sm font-semibold capitalize ${category === c ? "bg-brand text-white" : "border border-line bg-white text-ink-soft"}`}
          >
            {c}
          </button>
        ))}
      </div>

      <textarea
        data-testid="feedback-message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={t("feedbackPlaceholder")}
        className="mt-4 min-h-[140px] w-full rounded-xl border border-line bg-white p-3 text-[15px] outline-none focus:border-brand"
      />
      <input
        data-testid="feedback-contact"
        value={contact}
        onChange={(e) => setContact(e.target.value)}
        placeholder={t("feedbackContactPh")}
        className="mt-3 w-full rounded-xl border border-line bg-white p-3 text-[15px] outline-none focus:border-brand"
      />
      <Button data-testid="feedback-submit" onClick={submit} disabled={busy} className="mt-5 h-12 w-full rounded-full bg-brand text-base font-bold hover:bg-brand-dark">
        {busy ? <Loader2 className="animate-spin" /> : t("feedbackSend")}
      </Button>
    </div>
  );
}
